const flashData = {
    "7": {
      "Semester (5)": {
        "Foundation Engineering": 
  `⚡ Flash Revision: Foundation Engineering
  
  ⚡ Flash Revision: Slope Stability (Geotechnical Engineering)
  
  ⚡️ 1. Basics of Slopes
  - Slope: Inclined ground surface (natural or artificial).
  - Unrestrained Slope: Free-standing exposed surface.
  - Natural Slopes: Formed by nature (hills, cliffs).
  - Man-made Slopes: Created by excavation, embankments, dams, landfills, etc.
  
  ⚡️ 2. Types of Slopes
  - Infinite Slope: Long, uniform slope; e.g., hill face.
  - Finite Slope: Limited extent; e.g., embankment, dam.
  
  ⚡️ 3. Slope Stability & Importance
  - Slope Stability: Resistance to failure by sliding or collapse.
  - Why Important? Prevents dam/embankment failures and loss of life/property.
  - Slope Stability Analysis: Compare shear stress vs. shear strength on potential failure surfaces.
  
  ⚡️ 4. Causes of Slope Failure
  - Forces: Gravity, seepage, pore pressure, quakes, erosion, toe excavation.
  - Geological Factors: Weak layers, stratification, underground voids.
  - Water Effects: Increases pore pressure, causes erosion, reduces strength.
  
  ⚡️ 5. Factor of Safety (FoS)
  - Definition: FoS = Resisting Forces / Driving Forces.
  - Stable Slope: FoS > 1.
  - Block Movement FS: FS = μsN / F.
  
  ⚡️ 6. Driving vs. Resisting Forces
  - Driving: Gravity, seepage, earthquakes.
  - Resisting: Soil shear strength, vegetation roots, friction.
  
  ⚡️ 7. Types of Slope Failures
  - Rotational: Curved slip (circular or non-circular)
  - Toe Failure: Slip surface exits at toe
  - Slope Failure: Surface intersects slope above toe
  - Base Failure: Surface goes below base
  - Translational: Planar surface slide (layered soil)
  - Compound: Mix of rotational + translational
  - Wedge Failure: Block slides along inclined plane
  - Miscellaneous: Spreads, flows, irregular failures
  
  ⚡️ 8. Key Concepts & Formulas
  - Taylor’s Stability Number: Sn = c / (γH × N)
  - FoS (Cohesive Soil): FoS = c / (γH × Sn)
  - Shear Strength (Mohr-Coulomb): τ = c + σ tan(φ)
  - Weight of Wedge: W = γ × A
  
  ⚡️ 9. Analytical Methods
  - Bishop’s Method: Slices slope into vertical segments; includes inter-slice forces for FoS.
  
  ✅ Must-Know Flash Facts
  - Slope stability = resisting > driving forces.
  - Water increases risk of failure (pore pressure ↑, strength ↓).
  - FoS > 1 ⇒ Stable.
  - Rotational → circular slip; Translational → planar.
  - Infinite vs. Finite slopes: Based on length and soil uniformity.
  - Toe/Base/Wedge failures are geometry-dependent.








⚡ Flash Revision: Earth Pressure & Slope Stability

⚡️ 1. Critical Slip Surface
- Defined as the surface along which FoS is minimum.
- Represents the most likely failure surface.

⚡️ 2. Types of Slope Failures
- Toe Failure
- Slope Failure
- Base Failure
- Translational
- Wedge
- Compound
- Miscellaneous (flows, spreads)

⚡️ 3. Swedish Circle Method (Method of Slices)
- Divides soil mass into vertical slices.
- Calculates moments and forces to determine FoS.

⚡️ 4. Pore Water Pressure Effects
- Reduces effective stress & shear strength.
- Lowers slope stability; increases failure risk.

⚡️ 5. Groundwater Significance
- Adds weight, creates pore pressure, causes seepage forces.
- All of which reduce stability.

⚡️ 6. Earth Dam Slope Stability Conditions
1. Downstream slope under steady seepage.
2. Upstream slope under sudden drawdown.
3. Both slopes during/immediately after construction.
4. Upstream slope under submerged condition.

⚡️ 7. Steady Seepage in Dams
- Forms phreatic line.
- Soil below it is saturated and builds pore pressure.

⚡️ 8. Why Downstream Slope Is Critical
- Seepage acts outward (destabilizing).
- Upstream seepage acts inward (stabilizing).

⚡️ 9. Pore Pressure Consideration
- Included when using effective shear parameters.
- Diagrams modify equations like Bishop’s method.

⚡️ 10. Pore Pressure Calculation
- u = h × γw
- h = pressure head, γw = unit weight of water

⚡️ 11. Phreatic Line and Slice Weights
- Above: bulk density; below: saturated density.
- Pore pressure reduces effective normal force.

⚡️ 12. Bishop’s Modified Equation
- Includes pore pressure u.
- Uses pore pressure ratio ru for simplification.

⚡️ 13. Pore Pressure Ratio (ru)
- ru = pore pressure / total vertical stress
- Often assumed constant for homogeneous soil

⚡️ 14. Bishop & Morgenstern Method
- Uses charts for: c / (γH), D, phi', and beta
- Charts provide stability coefficients m and n

⚡️ 15. Sudden Drawdown Effect
- External water removed, internal pressure remains.
- Reduces slope stability in low-permeability soil.

⚡️ 16. Skempton’s Formula for Drawdown
- u = γ × [h + h*(1 − B) − h1]
- B = pore pressure coefficient (≈ 1)

⚡️ 17. Drawdown Ratio
- Ratio = drop in water level / slope height
- Used in Morgenstern stability charts

⚡️ 18. Morgenstern Method (Rapid Drawdown)
- Uses Bishop’s method with:
  - Homogeneous slope
  - Impermeable base
  - Fully submerged start
  - No pore pressure dissipation

⚡️ 19. Construction Phase Pore Pressure
- u = γw × (h − h1)
- Effective N = total normal force − pore pressure

⚡️ 20. Factors Influencing Construction Pore Pressure
- Water content, compaction, permeability, construction rate

⚡️ 21. Bishop’s Simplified Method
- Divides slope into vertical slices
- Considers circular slip + pore pressure

⚡️ 22. Slice Weight in Bishop’s Method
- W = γ × b × h
- Base stress = normal force − pore pressure

⚡️ 23. Lateral Earth Pressure Basics
- Soil pushes horizontally on walls
- Depends on wall movement, water, surcharge, etc.

⚡️ 24. Types of Lateral Earth Pressure
- Active
- Passive
- At Rest

⚡️ 25. Active Earth Pressure
- Wall moves away → pressure decreases
- Mohr’s circle expands to failure

⚡️ 26. Passive Earth Pressure
- Wall moves into soil → pressure increases
- Mohr’s circle compresses then expands

⚡️ 27. Earth Pressure at Rest
- Wall doesn’t move
- Static horizontal stress

⚡️ 28. Symbols Used
- pa = active pressure
- pp = passive pressure
- po = pressure at rest

⚡️ 29. Wall Movement Effects
- Moves away → active ↓
- Moves in → passive ↑
- No move → at-rest pressure

⚡️ 30. Mohr’s Circle Role
- Active: circle expands to touch failure line
- Passive: compresses then touches line

⚡️ 31. Factors Affecting Earth Pressure
- Includes wall friction, φ, cohesion, density, water, surcharge

⚡️ 32. Wall Friction
- Changes direction & magnitude of pressure

⚡️ 33. Examples of At-Rest Pressure
- Basement walls, fixed abutments

⚡️ 34. Direction of Forces
- Active: soil pushes wall outward
- Passive: soil resists inward movement

⚡️ 35. Limiting Equilibrium Conditions
- Active: backfill Mohr circle touches failure line
- Passive: front soil Mohr circle touches failure line

⚡️ 36. Shear Key Function
- Embedded below wall
- Generates passive pressure to resist sliding

⚡️ 37. Passive Pressure Role
- Always resisting/stabilizing force

⚡️ 38. At-Rest vs Active Pressure
- At-rest > active (due to no strain release)

⚡️ 39. Passive Pressure with Cohesion
- Pp = Kp × γ × H + 2c × √Kp

⚡️ 40. Location of Passive Thrust
- Found using centroid of pressure diagram

⚡️ 41. Active Pressure in Submerged Cohesive Backfill
- pa = Ku × γ' × h + 2c × √Ku + γw × h

⚡️ 42. Depth of Tension Crack
- ht = (2c × √Ku) / (Ku × γ' + γw)

⚡️ 43. Active Pressure at Base
- pa = Ku × γ' × H + 2c × √Ku + γw × H

⚡️ 44. Two-Layer Cohesive Backfill
- pa = K × γ × h − 2c × √K (for each layer)

⚡️ 45. Cohesionless Over Cohesive
- Top: pa = Ka1 × γ1 × h1
- Bottom: pa = Ka2 × γ2 × h2 − 2c × √Ka2

⚡️ 46. Rankine Coefficients
- Ka = (1 − sinφ) / (1 + sinφ)
- Kp = (1 + sinφ) / (1 − sinφ)

⚡️ 47. Active Thrust in Layered Backfill
- Sum areas of triangles/trapezoids from pressure-depth graph

⚡️ 48. Rankine Passive Pressure (Dry)
- pp = γ × h × tan²(45° + φ/2)

⚡️ 49. Resultant Thrust Location
- Triangle = 1/3rd height from base
- General = centroid of pressure diagram

⚡️ 50. Partially Submerged Cohesive Backfill
- pa = Ku × γ × h1 + Ku × γ' × h2 + γw × h2 − 2c × √Ku

⚡️ 51. Active Thrust in Stratified Backfill
- Draw pressure profile and compute area under it

⚡️ 52. Friction Angle Variability
- Different φ → different Ka
- Calculate layer-wise then combine

✅ Must-Know Flash Facts
- Active = wall moves away → lower pressure
- Passive = wall moves in → max resistance
- At-rest = no movement → intermediate stress
- Pore pressure ↓ stability in slopes and walls
- Use Bishop/Morgenstern charts for fast Fs estimates
- Effective stress = total stress − pore pressure








⚡ Flash Revision: Lateral Earth Pressure & Retaining Walls

⚡️ 1. Basics of Lateral Earth Pressure
- Horizontal soil pressure on retaining structures.
- Depends on soil type, wall movement, water table.

⚡️ 2. Types of Lateral Earth Pressure
- Active: wall moves away from backfill.
- Passive: wall moves toward soil.
- At Rest: no wall movement.

⚡️ 3. Rankine’s Theory – Assumptions
- Dry, cohesionless, isotropic, homogeneous soil.
- No wall friction, vertical wall, horizontal backfill.
- Plane strain condition assumed.

⚡️ 4. Rankine Coefficients
- Active: Ka = (1 − sinφ) / (1 + sinφ)
- Passive: Kp = (1 + sinφ) / (1 − sinφ)

⚡️ 5. Earth Pressure Expressions
- Active: pa = Ka × γ × h
- Passive: pp = Kp × γ × h

⚡️ 6. Total Thrust (Triangular)
- Active: Pa = (1/2) × Ka × γ × H², acts at H/3 from base
- Passive: Pp = (1/2) × Kp × γ × H², acts at H/3 from base

⚡️ 7. Cohesive Soil Adjustment
- Active: pa = K × γ × h − 2c × √K
- Passive: pp = K × γ × h + 2c × √K

⚡️ 8. Tension Crack
- Depth: ht = 2c / (γ × √Ka)
- Appears near top in cohesive backfill

⚡️ 9. Earth Pressure at Rest
- po = Ko × γ × h, where Ko = 1 − sinφ

⚡️ 10. Effect of Water Table
- Use submerged unit weight γ′
- Add water pressure: p = p_soil + γw × h

⚡️ 11. Surcharge Effect
- Uniform load adds: ps = Ka × q

⚡️ 12. Coulomb’s Theory
- Accounts for wall friction, wall/backfill inclination

⚡️ 13. Resultant Location
- Triangle: H/3 from base
- Trapezoid: centroid of area

⚡️ 14. Graphical Methods
- Rebhann and Culmann: determine magnitude & direction

⚡️ 15. Effect of Wall Friction (δ)
- Higher δ → active ↓, passive ↑

⚡️ 16. Inclined Backfill Pressure
- Use Coulomb or graphical methods

⚡️ 17. Retaining Structures – Uses
- Basements, slopes, highways, bridge abutments, etc.

⚡️ 18. Types of Retaining Walls
- Gravity, Semi-gravity, Cantilever, Counterfort, Buttress, Anchored, MSE

⚡️ 19. Gravity Wall
- Resists by self-weight
- Concrete or stone masonry; for small heights

⚡️ 20. Cantilever Wall
- Includes stem + heel + toe
- Economical up to H = 6–7 m

⚡️ 21. Counterfort Wall
- Triangular webs reduce bending in tall walls

⚡️ 22. Buttressed Wall
- Like counterfort, but supports at front (compression)

⚡️ 23. Drainage in Retaining Walls
- Prevents water pressure buildup
- Weep holes, drainage layers, geotextiles

⚡️ 24. Drainage Provisions
- Weep holes: 100 mm Ø, spaced 1–1.5 m
- Use filter media or pipes

⚡️ 25. Rankine vs. Coulomb
- Rankine: smooth vertical walls
- Coulomb: inclined/rough walls

⚡️ 26. Conjugate Failure Planes
- Define failure wedge in cohesionless soils
- Based on φ and backfill slope

⚡️ 27. Active Pressure in Cantilever Wall
- Pa acts on vertical face AB at H/3
- Combine with wedge weight

⚡️ 28. Retaining Wall Stability Checks
- Overturning, Sliding, Base tension, Bearing failure
- FoS ≥ 1.5 (sliding), 2–3 (bearing)

⚡️ 29. Resisting Moment (Overturning)
- Mr = W1 × x1 + W2 × x2 + ...

⚡️ 30. Sliding Resistance
- Fr = μ × N
- FoS = Fr / Ph ≥ 1.5

⚡️ 31. Base Tension Check
- Ensure e ≤ B/6
- Max pressure: pmax = (W / B) × (1 + 6e / B)

⚡️ 32. Bearing Capacity Check
- pmax ≤ q_allowable (FoS 2–3)

⚡️ 33. Typical Proportions
- Gravity: base = 0.5H to 0.7H
- Cantilever: slab ≈ 0.5H, stem ≥ 0.3 m
- Counterforts: spaced at 1/3–1/2 of height

✅ Must-Know Flash Facts
- Rankine: vertical, smooth walls
- Coulomb: inclined or rough, wall friction considered
- Cohesive backfill = tension cracks possible
- Stability = check all 4 modes (O.S.T.B.)
- Good drainage = safer wall





⚡ Flash Revision: Earth Pressure & Slope Stability

⚡️ 1. Critical Slip Surface
- Defined as the surface along which FoS is minimum.
- Represents the most likely failure surface.

⚡️ 2. Types of Slope Failures
- Toe Failure
- Slope Failure
- Base Failure
- Translational
- Wedge
- Compound
- Miscellaneous (flows, spreads)

⚡️ 3. Swedish Circle Method (Method of Slices)
- Divides soil mass into vertical slices.
- Calculates moments and forces to determine FoS.

⚡️ 4. Pore Water Pressure Effects
- Reduces effective stress & shear strength.
- Lowers slope stability; increases failure risk.

⚡️ 5. Groundwater Significance
- Adds weight, creates pore pressure, causes seepage forces.
- All of which reduce stability.

⚡️ 6. Earth Dam Slope Stability Conditions
1. Downstream slope under steady seepage.
2. Upstream slope under sudden drawdown.
3. Both slopes during/immediately after construction.
4. Upstream slope under submerged condition.

⚡️ 7. Steady Seepage in Dams
- Forms phreatic line.
- Soil below it is saturated and builds pore pressure.

⚡️ 8. Why Downstream Slope Is Critical
- Seepage acts outward (destabilizing).
- Upstream seepage acts inward (stabilizing).

⚡️ 9. Pore Pressure Consideration
- Included when using effective shear parameters.
- Diagrams modify equations like Bishop’s method.

⚡️ 10. Pore Pressure Calculation
- u = h × γw
- h = pressure head, γw = unit weight of water

⚡️ 11. Phreatic Line and Slice Weights
- Above: bulk density; below: saturated density.
- Pore pressure reduces effective normal force.

⚡️ 12. Bishop’s Modified Equation
- Includes pore pressure u.
- Uses pore pressure ratio ru for simplification.

⚡️ 13. Pore Pressure Ratio (ru)
- ru = pore pressure / total vertical stress
- Often assumed constant for homogeneous soil

⚡️ 14. Bishop & Morgenstern Method
- Uses charts for: c / (γH), D, phi', and beta
- Charts provide stability coefficients m and n

⚡️ 15. Sudden Drawdown Effect
- External water removed, internal pressure remains.
- Reduces slope stability in low-permeability soil.

⚡️ 16. Skempton’s Formula for Drawdown
- u = γ × [h + h*(1 − B) − h1]
- B = pore pressure coefficient (≈ 1)

⚡️ 17. Drawdown Ratio
- Ratio = drop in water level / slope height
- Used in Morgenstern stability charts

⚡️ 18. Morgenstern Method (Rapid Drawdown)
- Uses Bishop’s method with:
  - Homogeneous slope
  - Impermeable base
  - Fully submerged start
  - No pore pressure dissipation

⚡️ 19. Construction Phase Pore Pressure
- u = γw × (h − h1)
- Effective N = total normal force − pore pressure

⚡️ 20. Factors Influencing Construction Pore Pressure
- Water content, compaction, permeability, construction rate

⚡️ 21. Bishop’s Simplified Method
- Divides slope into vertical slices
- Considers circular slip + pore pressure

⚡️ 22. Slice Weight in Bishop’s Method
- W = γ × b × h
- Base stress = normal force − pore pressure

⚡️ 23. Lateral Earth Pressure Basics
- Soil pushes horizontally on walls
- Depends on wall movement, water, surcharge, etc.

⚡️ 24. Types of Lateral Earth Pressure
- Active
- Passive
- At Rest

⚡️ 25. Active Earth Pressure
- Wall moves away → pressure decreases
- Mohr’s circle expands to failure

⚡️ 26. Passive Earth Pressure
- Wall moves into soil → pressure increases
- Mohr’s circle compresses then expands

⚡️ 27. Earth Pressure at Rest
- Wall doesn’t move
- Static horizontal stress

⚡️ 28. Symbols Used
- pa = active pressure
- pp = passive pressure
- po = pressure at rest

⚡️ 29. Wall Movement Effects
- Moves away → active ↓
- Moves in → passive ↑
- No move → at-rest pressure

⚡️ 30. Mohr’s Circle Role
- Active: circle expands to touch failure line
- Passive: compresses then touches line

⚡️ 31. Factors Affecting Earth Pressure
- Includes wall friction, φ, cohesion, density, water, surcharge

⚡️ 32. Wall Friction
- Changes direction & magnitude of pressure

⚡️ 33. Examples of At-Rest Pressure
- Basement walls, fixed abutments

⚡️ 34. Direction of Forces
- Active: soil pushes wall outward
- Passive: soil resists inward movement

⚡️ 35. Limiting Equilibrium Conditions
- Active: backfill Mohr circle touches failure line
- Passive: front soil Mohr circle touches failure line

⚡️ 36. Shear Key Function
- Embedded below wall
- Generates passive pressure to resist sliding

⚡️ 37. Passive Pressure Role
- Always resisting/stabilizing force

⚡️ 38. At-Rest vs Active Pressure
- At-rest > active (due to no strain release)

⚡️ 39. Passive Pressure with Cohesion
- Pp = Kp × γ × H + 2c × √Kp

⚡️ 40. Location of Passive Thrust
- Found using centroid of pressure diagram

⚡️ 41. Active Pressure in Submerged Cohesive Backfill
- pa = Ku × γ' × h + 2c × √Ku + γw × h

⚡️ 42. Depth of Tension Crack
- ht = (2c × √Ku) / (Ku × γ' + γw)

⚡️ 43. Active Pressure at Base
- pa = Ku × γ' × H + 2c × √Ku + γw × H

⚡️ 44. Two-Layer Cohesive Backfill
- pa = K × γ × h − 2c × √K (for each layer)

⚡️ 45. Cohesionless Over Cohesive
- Top: pa = Ka1 × γ1 × h1
- Bottom: pa = Ka2 × γ2 × h2 − 2c × √Ka2

⚡️ 46. Rankine Coefficients
- Ka = (1 − sinφ) / (1 + sinφ)
- Kp = (1 + sinφ) / (1 − sinφ)

⚡️ 47. Active Thrust in Layered Backfill
- Sum areas of triangles/trapezoids from pressure-depth graph

⚡️ 48. Rankine Passive Pressure (Dry)
- pp = γ × h × tan²(45° + φ/2)

⚡️ 49. Resultant Thrust Location
- Triangle = 1/3rd height from base
- General = centroid of pressure diagram

⚡️ 50. Partially Submerged Cohesive Backfill
- pa = Ku × γ × h1 + Ku × γ' × h2 + γw × h2 − 2c × √Ku

⚡️ 51. Active Thrust in Stratified Backfill
- Draw pressure profile and compute area under it

⚡️ 52. Friction Angle Variability
- Different φ → different Ka
- Calculate layer-wise then combine

✅ Must-Know Flash Facts
- Active = wall moves away → lower pressure
- Passive = wall moves in → max resistance
- At-rest = no movement → intermediate stress
- Pore pressure ↓ stability in slopes and walls
- Use Bishop/Morgenstern charts for fast Fs estimates
- Effective stress = total stress − pore pressure





⚡ Flash Revision: Shallow Foundations & Bearing Capacity

⚡️ 1. Active Earth Pressure Components
- Includes: backfill weight, surcharge load, pore water pressure.
- Resultant force acts at 1/3 H from base.
- Formula (Rankine): Pa = (1/2) × Ka × γ × H²

⚡️ 2. Mechanically Stabilized Earth (MSE) Walls
- Use horizontal reinforcements (metal/geosynthetics) in fill.
- Transfers lateral earth pressure into tension in reinforcements.

⚡️ 3. Bearing Capacity
- Max pressure soil can support without failure.
- Foundation should not exceed this under applied loads.

⚡️ 4. Meyerhof’s Theory (1951)
- Considers shape, depth, and load inclination.
- Failure mechanism includes: elastic zone, radial shear, and log spiral shear zone.

⚡️ 5. Hansen’s Theory (1970)
- Adds base and ground slope effects.
- Useful for cohesive soils; assumes smooth base.

⚡️ 6. Vesic’s Theory (1973)
- Based on Terzaghi; modifies bearing factors.
- Better for analyzing elastic wedge behavior.

⚡️ 7. Ultimate Bearing Capacity Formula
- General: qu = cNc + qNq + 0.5γBNγ
- c = cohesion, q = surcharge (γDf), γ = unit weight, B = width

⚡️ 8. Meyerhof’s Correction Factors
- Apply shape, depth, inclination factors.
- Adjust φ (friction angle): φr = (1.1 − 0.1 × B/L) × φ

⚡️ 9. IS Code Method (IS 6403:1981)
- Uses Terzaghi-like formula with:
  - Water table correction
  - Eccentricity correction
  - Soil type adjustment

⚡️ 10. Water Table Effects
- W' = 1 (below Df + B), W' = 0.5 (at base).
- Reduces effective stress → lower bearing capacity.

⚡️ 11. Local Shear Failure
- For loose soils: use mobilized φ:
  - tan(φm) = (2/3) × tan(φ)

⚡️ 12. Eccentric Loading
- Effective width: B' = B − 2e
- If e > B/6 ⇒ tension may develop.

⚡️ 13. Safety Factors
- For bearing capacity: FS = 2.5–3.0
- Also apply FS for sliding, overturning, and settlement.

⚡️ 14. Bearing from SPT (N-values)
- Use avg. N over 1.5–2B depth.
- Remove extreme outliers (>50% above mean).
- Use empirical charts or IS 6403.

⚡️ 15. Bearing from CPT (qc)
- Use cone resistance qc from IS:4968 Part-III.
- Apply correction; average over foundation depth.

⚡️ 16. General vs. Local Shear
- General: dense soil, clear failure, high strength.
- Local: loose soil, slow deformation, lower capacity.

⚡️ 17. When to Use Which Method?
- Terzaghi: simple, shallow strip footings.
- Meyerhof: deeper + inclined load.
- Hansen: slope/inclination effects.
- Vesic: accurate, modifies stress zones.

⚡️ 18. Foundation Depth Importance
- Deeper = higher bearing, avoids moisture change, frost, erosion.

⚡️ 19. IS:1904 Depth Rule
- Minimum: 0.5 m (prefer 0.9 m for safety).

⚡️ 20. Avoiding Fill Soil
- Dumped fill = compressible → place foundation below it.

⚡️ 21. Groundwater Effects
- Reduces strength; needs dewatering.
- Prefer placing above GWT.

⚡️ 22. Frost Depth
- Place below frost line to prevent frost heave.
- Relevant only in cold/hilly areas.

⚡️ 23. Active Zone in Black Cotton Soil
- Depth: 1.5–2.5 m. Avoid placing foundation inside active zone.

⚡️ 24. IS:1904 Adjacent Structure Rule
- Adjacent foundations: slope < 2H:1V

⚡️ 25. Scour Depth (Bridges)
- Foundation depth = scour depth + grip length (below HFL)

⚡️ 26. Load Combinations (IS:1904)
- Dead + Live, or Dead + Live + Wind/Seismic.
- For cohesive soils: use 50% of live load.

⚡️ 27. Seismic Design (IS:1893)
- If seismic load ≥ 25% of DL+LL ⇒ SBC can be exceeded by 25%
- Must check for liquefaction and excessive settlement.

⚡️ 28. Preferred Footing Shapes
- Square: vertical loads
- Rectangular: columns with moment

⚡️ 29. FOS for Shallow Foundation
- Sliding ≥ 1.75
- Overturning ≥ 2.0 (1.5 for seismic/wind)

⚡️ 30. Min Width of Wall Footing
- Width = wall thickness + 30 cm

⚡️ 31. Strip (Wall) Footing
- Continuous footing under wall or close columns.

⚡️ 32. Spread/Isolated Footing
- Supports one column.
- Shapes: square, rectangle, or circle.

⚡️ 33. Combined Footing
- For 2+ columns if individual footings overlap or exceed property limit.

⚡️ 34. Rectangular vs. Trapezoidal Combined Footing
- Rectangular: similar loads.
- Trapezoidal: unequal loads.

⚡️ 35. Strap Footing
- Connects outer & inner footings via strap beam for load balance.

⚡️ 36. Raft/MAT Foundation
- One large slab supports all columns.
- Used on soft soil or where settlement is a concern.

⚡️ 37. Ring (Annular) Footing
- For circular structures (chimneys, tanks).
- Handles overturning and uplift.

⚡️ 38. Frost Zone Placement
- Prevent frost damage by placing below freezing depth.

⚡️ 39. Active Zone in Clay Soils
- Depth: 1.5–2.5 m for seasonal moisture variation.
- Place foundation below this.

⚡️ 40. Edge Distance for Sloped Ground
- Min edge distance = 60–90 cm.

⚡️ 41. Scour Depth + Grip Length (Bridges)
- Foundation = max scour depth + additional grip length for safety.

✅ Must-Know Flash Facts
- Use φm = 2/3 φ for local shear.
- Use effective width B' = B − 2e under eccentric loading.
- Foundation depth ↑ → SBC ↑ and stability ↑
- SPT/CPT methods are practical for field SBC estimation.
- FS (Bearing) = 2.5–3.0 | FS (Sliding) = 1.75 | FS (Overturning) = 2.0







⚡ Flash Revision: Foundation Engineering – Load & Settlement Concepts

⚡️ 1. Load Combinations
- Dead Load + Live Load
- Dead + Live + Wind/Seismic
- For cohesive soils: Use 50% of live load

⚡️ 2. Seismic Load Consideration
- If seismic load ≥ 25% of (DL + LL): SBC can be exceeded by 25%
- Check liquefaction and settlement (IS:1893-2002)

⚡️ 3. Footing Shape Preferences
- Square: vertical loads
- Rectangular: columns with moment loads

⚡️ 4. Minimum FOS (Factor of Safety)
- Sliding: ≥ 1.75
- Overturning: ≥ 2.0
- Wind/Seismic: 1.5 for both

⚡️ 5. Minimum Footing Width
- Wall footing = wall thickness + 30 cm

⚡️ 6. Angular Load Spread
- Masonry: 2 vertical to 1 horizontal
- Lime concrete: 3 vertical to 2 horizontal
- Cement concrete: 1 vertical to 1 horizontal

⚡️ 7. Types of Footings
- Strip, Isolated, Combined, Strap, Mat/Raft, Ring/Annular

⚡️ 8. Combined Footing Shapes
- Rectangular: when column loads are nearly equal
- Trapezoidal: when one column carries much more load

⚡️ 9. Types of Settlement
- Total Settlement = Immediate (Se) + Primary Consolidation (Sc) + Secondary Consolidation (Ss)

⚡️ 10. Immediate Settlement Formula
- Se = (q × B × If) / Es
  where:
  q = applied pressure,
  B = width,
  If = influence factor,
  Es = modulus of elasticity

⚡️ 11. Primary Consolidation Settlement Formula
- Sc = (Cc / (1 + e0)) × H × log(p1 / p0)
  where:
  Cc = compression index,
  e0 = initial void ratio,
  H = thickness of clay layer,
  p1 = final pressure,
  p0 = initial pressure

⚡️ 12. Secondary Settlement Formula
- Ss = Ca × H × log(t2 / t1)
  where:
  Ca = secondary compression index,
  H = thickness,
  t1 and t2 = time intervals

⚡️ 13. Other Causes of Settlement
- Groundwater drop, erosion, frost, vibrations, mining

⚡️ 14. Settlement Analysis Steps (IS:8009-1976)
- Site data → Soil profile → Stress analysis → Estimate settlement and time

⚡️ 15. Stress Distribution Theories
- Boussinesq for normally consolidated clay (NCC)
- Westergaard for overconsolidated clay (OCC)

⚡️ 16. Schmertmann’s Method
- For Es: use 2.5 × qc (if L/B < 10), 3.5 × qc (if L/B > 10), or 800 × N (from SPT)

⚡️ 17. SPT-Based Settlement (IS:8009)
- Uses average N-values and groundwater correction

⚡️ 18. Plate Load Test (IS:1888-1982)
- Used to estimate load vs. settlement curve on site

⚡️ 19. Plate Sizes Used
- 30 cm, 45 cm, 60 cm, or 75 cm
- Thickness ≥ 25 mm

⚡️ 20. PLT Interpretation
- Use bilinear or log-log curve to find ultimate load

⚡️ 21. Correction Factors
- Depth correction: Fox method
- Rigidity: multiply flexible settlement by 0.8

⚡️ 22. Differential Settlement
- Uneven settlement → tilting and structural damage

⚡️ 23. Angular Distortion Formula
- T = δ / L
  where:
  δ = differential settlement,
  L = horizontal distance between columns

⚡️ 24. Allowable Settlement
- Generally ≤ 25 mm for columns (IS:1904)

⚡️ 25. What is a Pile Foundation?
- Deep foundation used when shallow foundation is not feasible

⚡️ 26. Pile Shapes
- Square, circular, H-section, pipe

⚡️ 27. Pile Materials
- Timber, steel, concrete, composite

⚡️ 28. Pile Installation Types
- Driven piles (displacement), Bored piles (non-displacement)

⚡️ 29. Load Transfer Mechanisms
- End-bearing: load transferred to hard layer at tip
- Friction: load carried by skin of pile

⚡️ 30. Pile Capacity Formula
- Qu = (qpu × Ab) + (fs × As)
  where:
  qpu = base resistance,
  Ab = base area,
  fs = skin friction,
  As = surface area

⚡️ 31. Skin Friction Formulas
- For sand: fs = q' × K × tan(δ)
- For clay: fs = α × cu

⚡️ 32. Critical Depth
- After 15 to 20 times diameter (D), resistance becomes constant

⚡️ 33. IS:2911-2010 Capacity Formulas
- Sand: Qu = q' × Nq × Ab + γ × B × Nγ × Ab
- Clay: Qu = α × cu × As + cu × b × Nc × Ab

⚡️ 34. Modified Hiley Formula for Driven Piles
- Qu = (W × h × η × α) / (S + C/2)

⚡️ 35. Types of Pile Load Tests
- Initial, routine, lateral, cyclic, pull-out

⚡️ 36. Vertical Load Test (IS:2911)
- Allowable load is lesser of:
  - Two-thirds of load at 12 mm settlement
  - 50% of load at 10% of pile dia

⚡️ 37. Pile Group Settlement
- Total settlement = pile compression + soil settlement

⚡️ 38. Pile Group Efficiency Formula
- ηg = Qu (group) / (N × Qu single pile)

⚡️ 39. Minimum Spacing of Piles
- End-bearing: 2.5D to 3.5D
- Friction piles: ≥ 3D
- Compaction piles: ~2D

⚡️ 40. Negative Skin Friction (NSF)
- Downward drag from surrounding soil

⚡️ 41. NSF Formulas
- For clay: Fn = P × Ln × α × cu
- For sand: Fn = 0.5 × P × Ln × γ × K × tan(δ)

✅ Must-Know Flash Facts
- Use rectangular footing if moment load is high
- PLT and SPT are practical tests for site design
- Pile groups are less efficient at close spacing
- Negative skin friction adds to axial pile load
- Footing width and depth depend on load and soil



  `
      },
    },
  };
  
  export default flashData;
  